import { Component, OnInit, AfterViewInit, HostListener, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ClientService } from '../../../../../../services/Client/client.service';
import { Client } from '../../../../../../models/client';
import { TresorerieService } from '../../../../../../services/tresorerie/tresorerie.service';
import { Tresorerie } from '../../../../../../models/Tresorerie/Tresorerie';
import { StationService } from '../../../../../../services/station.service';
import { DocumentService } from '../../../../../../services/document/document.service';
import { PaiementService } from '../../../../../../services/paiement/paiement.service';
import { environment } from '../../../../../../../environments/environment';
import * as Feather from 'feather-icons';
import flatpickr from 'flatpickr';
import { French } from 'flatpickr/dist/l10n/fr.js';

export interface ReleveTransaction {
  etape?: string;
  date: string;
  transaction: string;
  details: string;
  credit: string;
  debit: string;
  solde: string;
}

const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const MONTHS_SHORT = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

@Component({
  selector: 'app-details-contenue-releve-compte',
  templateUrl: './details-contenue-releve-compte.component.html',
  styleUrls: ['./details-contenue-releve-compte.component.scss']
})
export class DetailsContenueReleveCompteComponent implements OnInit, AfterViewInit {

  client: Client | null = null;
  isLoading = false;

  @ViewChild('datePicker') datePicker!: ElementRef<HTMLInputElement>;

  viewMode: 'jours' | 'mois' | 'annees' = 'mois';
  showDatePicker = false;
  showViewDropdown = false;
  showYearDropdown = false;
  /** Popup Jours / Mois / Année : 'jours' = jours de la semaine, 'mois' = 12 mois, 'annees' = années */
  showPeriodPopup: 'jours' | 'mois' | 'annees' | null = null;
  weekDays: { label: string; dateString: string; isToday: boolean }[] = [];
  weekDayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  currentMonthIndex = new Date().getMonth();
  currentYear = new Date().getFullYear();
  selectedMonthIndex: number = new Date().getMonth();
  selectedDay = '';
  selectedYear = new Date().getFullYear();
  selectedDate: Date = new Date();
  selectedPeriod = '';

  monthsShort = MONTHS_SHORT;
  years: number[] = [];

  selectedMonth = new Date().getMonth() + 1;
  monthLabel = MONTHS_FR[new Date().getMonth()];
  dateDu = '';
  dateAu = '';
  periodShort = '';

  companyName = 'VShopi';
  companyAddress = 'Tunisie';
  companyPhones = '+1 (123) 456 7891, +44 (876) 543 2198';

  stationData: { nom_entreprise: string; logo: string } = { nom_entreprise: '', logo: '' };

  clientName = '--';
  clientAddress = '--';
  soldeInitial = 150000;
  solde = 100000;
  transactions: ReleveTransaction[] = [];
  /** Pagination : liste affichée pour la page courante */
  paginatedTransactions: ReleveTransaction[] = [];
  currentPage = 1;
  pageSize = 10;
  totalPages = 1;
  pages: number[] = [];
  readonly pageSizeOptions = [10, 25, 50, 100];

  /** Ids des statuts "avoir" (Utilisé, Non utilisé, Partiellement utilisé) — chargés via API, fallback [18,19,20]. */
  statutsAvoirIds: number[] = [18, 19, 20];
  /** Id du statut "Payé" — chargé via API, fallback 5. */
  statutPayeId: number = 5;
  /** Id du type "FACTURE" (récupéré via GET /api/statut-documents), fallback 11. */
  typeDocumentFactureId: number | null = null;
  /** Solde avances (somme des montants trésorerie avance_espece actives). */
  clientAvance = 0;
  /** Lignes avance en espèce depuis la trésorerie (type_operation avance_espece). */
  avancesList: Tresorerie[] = [];
  isLoadingAvances = false;

  constructor(
    private route: ActivatedRoute,
    private clientService: ClientService,
    private stationService: StationService,
    private documentService: DocumentService,
    private paiementService: PaiementService,
    private tresorerieService: TresorerieService,
    private el: ElementRef
  ) {}

  ngOnInit(): void {
    const reference = this.route.parent?.snapshot.paramMap.get('reference');
    if (reference && reference !== 'add') {
      this.loadClient(reference);
    } else {
      this.transactions = [];
      this.updatePagination();
    }
    this.years = Array.from({ length: 10 }, (_, i) => this.currentYear - 5 + i);
    this.generateWeekDays();
    this.updatePeriodFromSelection();
    this.loadStationData();
  }

  /** Station + utilisateur connectés (aligné stock / trésorerie : APIs avec station_id & user_id). */
  private getSessionFromStorage(): { stationId: number; userId: number } {
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      const userId = Number(currentUser?.id) || 0;
      const station = currentUser?.stations?.[0];
      const stationId = Number(station?.station_id ?? station?.id ?? currentUser?.station_id) || 0;
      return { stationId, userId };
    } catch {
      return { stationId: 0, userId: 0 };
    }
  }

  private getStationIdFromStorage(): number {
    return this.getSessionFromStorage().stationId;
  }

  /** Avances en espèce depuis la trésorerie (GET /api/tresorerie?type_operation=avance_espece&client_id=…). */
  loadAvancesClient(clientId: number, stationId: number): void {
    if (!clientId || !stationId) {
      this.avancesList = [];
      this.clientAvance = 0;
      return;
    }
    this.isLoadingAvances = true;
    this.tresorerieService.getAvancesEspeceClient(clientId, stationId).subscribe({
      next: (list: Tresorerie[]) => {
        this.avancesList = Array.isArray(list) ? list : [];
        this.clientAvance = this.sumAvancesMontant(this.avancesList);
        this.isLoadingAvances = false;
      },
      error: () => {
        this.avancesList = [];
        this.clientAvance = 0;
        this.isLoadingAvances = false;
      }
    });
  }

  private sumAvancesMontant(ops: Tresorerie[]): number {
    return ops.reduce((s, op) => s + (parseFloat(String(op.montant ?? '0')) || 0), 0);
  }

  loadStationData(): void {
    const stationId = this.getStationIdFromStorage();
    if (!stationId) return;
    this.stationService.getStation(stationId).subscribe({
      next: (res) => {
        if (res?.success && res?.station) {
          const s = res.station;
          this.stationData.nom_entreprise = s.nom_entreprise || s.label || this.companyName;
          if (s.logo) {
            this.stationData.logo = s.logo.startsWith('http') ? s.logo : `${environment.url_images}/${s.logo}`.replace(/([^:]\/)\/+/g, '$1');
          }
          if (s.adresses?.[0]) {
            const a = s.adresses[0];
            this.companyAddress = [a.adresse, a.code_postal, a.pays].filter(Boolean).join(', ') || this.companyAddress;
          }
          if (s.telephones?.length) {
            this.companyPhones = s.telephones.map((t: any) => t.numero).filter(Boolean).join(', ') || this.companyPhones;
          }
        }
      },
      error: () => {}
    });
  }

  ngAfterViewInit(): void {
    Feather.replace();
    this.setCalendarIconWhite();
    setTimeout(() => this.initializeFlatpickr(), 100);
  }

  private initializeFlatpickr(): void {
    if (!this.datePicker?.nativeElement) return;
    const el = this.datePicker.nativeElement;
    const firstDay = new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth(), 1);
    const lastDay = new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth() + 1, 0);
    const fp = flatpickr(el, {
      locale: French,
      mode: 'range',
      dateFormat: 'Y-m-d',
      defaultDate: [firstDay, lastDay],
      allowInput: false,
      onChange: (selectedDates: Date[]) => {
        if (selectedDates.length === 1) {
          this.dateDu = this.formatDateFr(selectedDates[0]);
          this.dateAu = this.formatDateFr(selectedDates[0]);
          this.selectedDate = new Date(selectedDates[0]);
          this.selectedMonthIndex = this.selectedDate.getMonth();
          this.selectedYear = this.selectedDate.getFullYear();
          this.updatePeriodFromSelection();
        } else if (selectedDates.length === 2) {
          this.updatePeriodFromRange(selectedDates[0], selectedDates[1]);
        }
      },
      onClose: (selectedDates: Date[]) => {
        if (selectedDates.length === 1) {
          this.updatePeriodFromRange(selectedDates[0], selectedDates[0]);
        }
      }
    });
    (el as any)._flatpickr = fp;
  }

  openCalendarOnly(): void {
    this.showViewDropdown = false;
    this.showYearDropdown = false;
    const fp = (this.datePicker?.nativeElement as any)?._flatpickr;
    if (fp) {
      const start = this.parseDateFr(this.dateDu) || new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth(), 1);
      const end = this.parseDateFr(this.dateAu) || new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth() + 1, 0);
      fp.setDate([start, end], false);
      fp.open();
      // Ne mettre que le z-index pour qu'il s'affiche au-dessus. Ne pas forcer display/visibility/opacity
      // sinon Flatpickr ne peut plus masquer le calendrier au clic extérieur (fp.close()).
      setTimeout(() => {
        const calendar = document.querySelector('.flatpickr-calendar');
        if (calendar) {
          (calendar as HTMLElement).style.zIndex = '99999';
        }
      }, 50);
    }
    setTimeout(() => {
      Feather.replace();
      this.setCalendarIconWhite();
    }, 0);
  }

  /** Force l'icône calendrier du bouton à être blanche (Feather génère un SVG) */
  private setCalendarIconWhite(): void {
    const btn = this.el.nativeElement.querySelector('.calendar-btn-blue');
    if (btn) {
      const svg = btn.querySelector('svg');
      if (svg) {
        svg.setAttribute('stroke', '#fff');
        svg.setAttribute('color', '#fff');
        svg.querySelectorAll('path, line, circle').forEach((el: SVGElement) => {
          el.setAttribute('stroke', '#fff');
        });
      }
    }
  }

  private loadClient(reference: string): void {
    this.isLoading = true;
    const id = /^\d+$/.test(reference) ? parseInt(reference, 10) : reference;
    this.clientService.getClientById(id).subscribe({
      next: (data: Client) => {
        this.client = data;
        this.clientName = data.nom_prenom || data.nom_entreprise || '--';
        const addr = data.adresses?.[0];
        if (addr) {
          this.clientAddress = [addr.adresse, addr.code_postal, addr.pays].filter(Boolean).join(', ') || '--';
        }
        if (data.solde_avance != null) {
          const av = typeof data.solde_avance === 'string' ? parseFloat(data.solde_avance) : Number(data.solde_avance);
          this.solde = av;
        }
        this.clientAvance = 0;
        this.soldeInitial = 0;
        this.loadAvancesClient(data.id!, this.getStationIdFromStorage());
        forkJoin({
          typeFacture: this.documentService.getTypeDocumentIdByTitre('FACTURE').pipe(catchError(() => of(null))),
          statuts: this.documentService.getStatutIdsForReleveClient()
        }).subscribe(({ typeFacture, statuts }) => {
          this.typeDocumentFactureId = typeFacture ?? 11;
          this.statutsAvoirIds = statuts.statutsAvoirIds;
          this.statutPayeId = statuts.statutPayeId ?? 5;
          this.loadReleveFromDocuments();
        });
        this.isLoading = false;
      },
      error: () => {
        this.transactions = [];
        this.isLoading = false;
      }
    });
  }

  loadReleveFromDocuments(): void {
    const clientId = this.client?.id;
    if (clientId == null) {
      this.transactions = [];
      return;
    }
    const dDu = this.parseDateFr(this.dateDu);
    const dAu = this.parseDateFr(this.dateAu);
    if (!dDu || !dAu) {
      this.transactions = [];
      return;
    }
    const periodStart = new Date(dDu);
    periodStart.setHours(0, 0, 0, 0);
    const periodEnd = new Date(dAu);
    periodEnd.setHours(23, 59, 59, 999);
    const tsFrom = periodStart.getTime();
    const tsTo = periodEnd.getTime();

    const filterByDateAndType = (list: any[]) =>
      list.filter(doc => {
        const docClientId = doc.client_id ?? doc.client?.id;
        if (docClientId != null && Number(docClientId) !== Number(clientId)) return false;
        const typeId = doc.type_document_id ?? doc.typeDocument?.id;
        const typeIdNum = typeId != null ? Number(typeId) : null;
        const titre = (doc.typeDocument?.titre || doc.type_document?.titre || '').toUpperCase();
        const statutId = doc.statut_id ?? doc.statut?.id;
        const statutIdNum = statutId != null ? Number(statutId) : null;
        const typeFactureId = this.typeDocumentFactureId ?? 11;
        const isAvoir =
          (statutIdNum != null && this.statutsAvoirIds.includes(statutIdNum)) ||
          titre.includes('AVOIR');
        const isFacture =
          typeIdNum === typeFactureId ||
          (titre.includes('FACTURE') && !titre.includes('AVOIR'));
        if (!isFacture && !isAvoir) return false;
        const docDate = doc.date || doc.date_creation || doc.created_at;
        if (!docDate) return true;
        const t = new Date(docDate).getTime();
        return t >= tsFrom && t <= tsTo;
      });

    // Tous les documents du client + documents statut Payé (au cas où l’API ne les renvoie pas par défaut)
    console.log('═══════════════════════════════════════════════════════════');
    console.log('[RELEVE] Appels API — clientId:', clientId, '| Période:', this.dateDu, '→', this.dateAu);
    console.log('[RELEVE] 1) GET /api/documents/client/' + clientId + ' + query station_id & user_id (session)');
    console.log('[RELEVE] 2) POST /api/documents/by-client-statut — même session en body si présent');
    console.log('═══════════════════════════════════════════════════════════');

    const { stationId, userId } = this.getSessionFromStorage();
    const docSession =
      stationId > 0 || userId > 0 ? { station_id: stationId, user_id: userId } : undefined;
    forkJoin({
      all: this.documentService.getDocumentsByClientAll(clientId, 200, docSession).pipe(
        catchError((err) => {
          console.error('[RELEVE] Erreur getDocumentsByClientAll:', err);
          return of([]);
        })
      ),
      paye: this.documentService.getDocumentsByClientStatutAll(clientId, this.statutPayeId ?? 5, 100, docSession).pipe(
        catchError((err) => {
          console.error('[RELEVE] Erreur getDocumentsByClientStatutAll:', err);
          return of([]);
        })
      ),
      avances: this.tresorerieService.getAvancesEspeceClient(clientId, stationId || undefined).pipe(
        catchError((err) => {
          console.error('[RELEVE] Erreur getAvancesEspeceClient:', err);
          return of([] as Tresorerie[]);
        })
      )
    }).pipe(
      map(({ all, paye, avances }) => {
        const avancesList = Array.isArray(avances) ? avances : [];
        console.log('[RELEVE] Résultat API — all (client):', all?.length ?? 0, 'documents | paye (statut 5):', paye?.length ?? 0, 'documents | avances:', avancesList.length);
        const byId = new Map<number, any>();
        [...all, ...paye].forEach(doc => {
          const id = doc?.id;
          if (id != null) byId.set(Number(id), doc);
        });
        const merged = Array.from(byId.values());
        const filtered = filterByDateAndType(merged);
        console.log('[RELEVE] Après fusion (par id):', merged.length, 'documents uniques');
        console.log('[RELEVE] Après filtre (date + type facture/avoir):', filtered.length, 'documents');
        if (filtered.length > 0) {
          console.log('[RELEVE] IDs documents retenus:', filtered.map((d: any) => d.id));
          console.log('[RELEVE] Réfs documents retenus:', filtered.map((d: any) => d.ref_externe ?? d.reference ?? '#').join(', '));
          console.log('[RELEVE] Statuts documents retenus:', filtered.map((d: any) => (d.statut_id ?? d.statut?.id) + ' (' + (d.statut?.titre ?? d.statut_id) + ')'));
        }
        return { docs: filtered, avancesList };
      }),
      switchMap(({ docs, avancesList }) => {
        console.log('[RELEVE] Chargement paiements pour', docs.length, 'documents...');
        if (docs.length === 0) {
          const emptyLines = this.buildReleveLines([], [], periodStart, periodEnd, avancesList);
          return of({ lines: emptyLines });
        }
        const paiementCalls = docs.map(d => this.paiementService.getPaiementsByDocumentId(d.id).pipe(catchError(() => of([]))));
        return forkJoin(paiementCalls).pipe(
          map((paiementResponses: any[]) => {
            console.log('[RELEVE] Paiements reçus —', paiementResponses.length, 'réponses (une par document)');
            paiementResponses.forEach((resp: any, i: number) => {
              const list = Array.isArray(resp) ? resp : (resp?.data?.paiements ?? (Array.isArray(resp?.data) ? resp.data : []));
              const nb = Array.isArray(list) ? list.length : 0;
              if (nb > 0) console.log('[RELEVE]   doc id', docs[i]?.id, (docs[i]?.ref_externe ?? docs[i]?.reference), '→', nb, 'paiement(s)');
            });
            return { docs, paiementsByDoc: paiementResponses, avancesList };
          })
        );
      })
    ).subscribe({
      next: (result: any) => {
        let lines: ReleveTransaction[] = [];
        if (result?.lines) {
          lines = result.lines;
        } else if (result?.docs != null && result?.paiementsByDoc != null) {
          const avancesList = result.avancesList ?? [];
          lines = this.buildReleveLines(result.docs, result.paiementsByDoc, periodStart, periodEnd, avancesList);
        }
        console.log('[RELEVE] Lignes relevé construites:', lines.length);
        this.transactions = lines;
        if (lines.length > 0) {
          const lastSolde = lines[lines.length - 1].solde;
          const match = lastSolde ? lastSolde.replace(/\s*TND$/, '').replace(/\s/g, '').replace(',', '.') : '';
          const n = parseFloat(match);
          if (!isNaN(n)) this.solde = n;
        }
        this.currentPage = 1;
        this.updatePagination();
        console.log('═══════════════════════════════════════════════════════════');
      },
      error: (err) => {
        console.error('[RELEVE] Erreur API:', err);
        console.error('[RELEVE] Erreur détail:', err?.error ?? err?.message);
        this.transactions = [];
        this.updatePagination();
        console.log('═══════════════════════════════════════════════════════════');
      }
    });
  }

  /** Met à jour la liste paginée et les numéros de page. */
  updatePagination(): void {
    const total = this.transactions.length;
    this.totalPages = total > 0 ? Math.ceil(total / this.pageSize) : 1;
    this.currentPage = Math.min(Math.max(1, this.currentPage), this.totalPages);
    this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
    const start = (this.currentPage - 1) * this.pageSize;
    this.paginatedTransactions = this.transactions.slice(start, start + this.pageSize);
  }

  changePage(page: number): void {
    this.currentPage = page;
    this.updatePagination();
  }

  get paginationStartIndex(): number {
    return this.transactions.length === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }

  get paginationEndIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.transactions.length);
  }

  private buildReleveLines(docs: any[], paiementsByDoc: any[], periodStart: Date, periodEnd: Date, avancesList: Tresorerie[] = []): ReleveTransaction[] {
    interface ReleveEvent { etape?: string; date: Date; transaction: string; details: string; credit: number; debit: number; /** Ligne informative : afficher crédit/débit mais ne pas modifier le solde. */ infoOnly?: boolean; }
    const events: ReleveEvent[] = [];
    const ref = (d: any) => d?.ref_externe ?? d?.reference ?? (d ? `#${d.id}` : '');
    const dateEcheance = (d: any) => {
      if (!d) return '';
      const str = d.date_echeance ?? d.date_limite_paiement;
      if (!str) return '';
      const dd = new Date(str);
      return isNaN(dd.getTime()) ? '' : this.formatDateFr(dd);
    };
    const docById = new Map<number, any>(docs.map(d => [d.id, d]));
    /** Factures dont les paiements sont déjà affichés dans un bloc avoir (éviter doublon). */
    const facturePaymentsShownInAvoir = new Set<number>();

    avancesList.forEach((a: Tresorerie) => {
      if (a.type_operation != null && a.type_operation !== 'avance_espece') return;
      const montant = parseFloat(String(a.montant ?? '0')) || 0;
      if (montant <= 0) return;
      const raw = a.date_operation || a.created_at;
      const dateAvance = raw ? new Date(raw) : periodStart;
      const t = dateAvance.getTime();
      if (t < periodStart.getTime() || t > periodEnd.getTime()) return;
      const details = (a.note || a.description || '').toString().trim() || 'Avance en espèce';
      events.push({ etape: 'avance', date: dateAvance, transaction: 'Avance', details, credit: montant, debit: 0 });
    });

    // Trier les documents par date puis factures avant avoirs (pour avoirs après leur facture liée)
    const sortedDocs = docs.slice().sort((a, b) => {
      const da = new Date(a.date || a.date_creation || a.created_at || 0).getTime();
      const db = new Date(b.date || b.date_creation || b.created_at || 0).getTime();
      if (da !== db) return da - db;
      const typeA = (a.type_document_id ?? a.typeDocument?.id) != null ? Number(a.type_document_id ?? a.typeDocument?.id) : 0;
      const typeB = (b.type_document_id ?? b.typeDocument?.id) != null ? Number(b.type_document_id ?? b.typeDocument?.id) : 0;
      const typeFactureId = this.typeDocumentFactureId ?? 11;
      const isAvoirA = typeA !== typeFactureId || (a.statut_id != null && this.statutsAvoirIds.includes(Number(a.statut_id)));
      const isAvoirB = typeB !== typeFactureId || (b.statut_id != null && this.statutsAvoirIds.includes(Number(b.statut_id)));
      if (isAvoirA !== isAvoirB) return isAvoirA ? 1 : -1;
      return (a.document_original_id ?? 0) - (b.document_original_id ?? 0);
    });

    sortedDocs.forEach((doc, sortedIdx) => {
      const origIdx = docs.indexOf(doc);
      const idx = origIdx >= 0 ? origIdx : sortedIdx;
      const paiementResp = paiementsByDoc[idx] ?? [];
      const paiements = this.normalizePaiementsList(paiementResp);
      const netPayer = parseFloat(doc.net_payer ?? doc.total_ttc ?? '0') || 0;
      const docTitre = (doc.typeDocument?.titre || doc.type_document?.titre || '').toUpperCase();
      const statutId = doc.statut_id ?? doc.statut?.id;
      const statutIdNum = statutId != null ? Number(statutId) : null;
      const isStatutAvoir = statutIdNum != null && this.statutsAvoirIds.includes(statutIdNum);
      const isAvoir = isStatutAvoir || (!(doc as any)._releveAsFacture && docTitre.includes('AVOIR'));
      const docDateStr = doc.date || doc.date_creation || doc.created_at;
      const docDate = docDateStr ? new Date(docDateStr) : periodStart;

      if (netPayer <= 0 && !isAvoir) return;

      if (isAvoir) {
        // Bloc avoir : 1) Avoir (crédit pur, augmente le solde)  2) Facture liée (info, débit 0)  3) Utilisation avoir (débit) + Paiement par avoir (crédit) — pas de ligne Espèce/Chèque
        events.push({ etape: 'création avoir', date: docDate, transaction: 'Avoir', details: `${ref(doc)} dû le ${dateEcheance(doc) || this.formatDateFr(docDate)}`, credit: netPayer, debit: 0 });
        let factureLiee: any = null;
        let factureLieePaiements: any[] = [];
        for (let fi = 0; fi < docs.length; fi++) {
          const otherDoc = docs[fi];
          const otherPaiements = this.normalizePaiementsList(paiementsByDoc[fi] ?? []);
          const hasAvoirForThis = otherPaiements.some((p: any) => Number(p.avoir_id) === Number(doc.id));
          if (hasAvoirForThis) {
            factureLiee = otherDoc;
            factureLieePaiements = otherPaiements;
            facturePaymentsShownInAvoir.add(otherDoc.id);
            break;
          }
        }
        const factureLieeRef = factureLiee ? ref(factureLiee) : (doc.document_original_id != null ? ref(docById.get(Number(doc.document_original_id))) || `#${doc.document_original_id}` : '');
        // Paiement par avoir : une seule ligne "Utilisation d'un avoir #ref facture" avec le montant en DÉBIT
        factureLieePaiements.forEach((p: any) => {
          const dateP = new Date(p.date_paiement || p.created_at || p.date);
          if (Number(p.avoir_id) !== Number(doc.id)) return;
          (p.lignes_paiement || []).forEach((ligne: any) => {
            const typeP = (ligne.type_paiement || p.type_paiement || '').toString().toLowerCase();
            if (typeP !== 'avoir') return;
            const mt = parseFloat(ligne.montant || '0') || 0;
            if (mt <= 0) return;
            events.push({ etape: "utilisation avoir", date: dateP, transaction: "Utilisation d'un avoir", details: `Utilisation d'un avoir ${factureLieeRef}`, credit: 0, debit: mt });
          });
        });
        // Retenue à la source uniquement ; pas de lignes Espèce/Chèque/Virement/Traite (avoir = compensation, pas de mouvement caisse)
        factureLieePaiements.forEach((p: any) => {
          const dateP = new Date(p.date_paiement || p.created_at || p.date);
          if (p.retenue_source && p.Montant_RS != null && parseFloat(String(p.Montant_RS)) > 0) {
            const mtRs = parseFloat(String(p.Montant_RS)) || 0;
            const tauxRs = p.Taux_RS != null ? ` (${p.Taux_RS}%)` : '';
            events.push({ etape: 'paiement', date: dateP, transaction: 'Retenu à la source', details: `Retenue à la source${tauxRs} pour le paiement de ${factureLieeRef}`, credit: mtRs, debit: 0 });
          }
        });
        return;
      }

      // Bloc facture en série : Facture (validation facture) → Retenue (paiement) → Paiements (paiement)
      events.push({ etape: 'validation facture', date: docDate, transaction: 'Facture', details: `${ref(doc)} dû le ${dateEcheance(doc) || this.formatDateFr(docDate)}`, credit: 0, debit: netPayer });
      if (facturePaymentsShownInAvoir.has(doc.id)) return;
      paiements.forEach((p: any) => {
        const dateP = new Date(p.date_paiement || p.created_at || p.date);
        if (p.retenue_source && p.Montant_RS != null && parseFloat(String(p.Montant_RS)) > 0) {
          const mtRs = parseFloat(String(p.Montant_RS)) || 0;
          const tauxRs = p.Taux_RS != null ? ` (${p.Taux_RS}%)` : '';
          events.push({ etape: 'paiement', date: dateP, transaction: 'Retenu à la source', details: `Retenu à la source${tauxRs} pour le paiement de ${ref(doc)}`, credit: mtRs, debit: 0 });
        }
        (p.lignes_paiement || []).forEach((ligne: any) => {
          const typeP = (ligne.type_paiement || p.type_paiement || 'espece').toString().toLowerCase();
          if (typeP === 'avoir') return;
          const mt = parseFloat(ligne.montant || '0') || 0;
          if (mt <= 0) return;
          const labelP = typeP === 'espece' ? 'Espèce' : typeP === 'cheque' ? 'Chèque' : typeP === 'virement' ? 'Virement' : typeP === 'traite' ? 'Traite' : typeP;
          events.push({ etape: 'paiement', date: dateP, transaction: labelP, details: `${labelP} pour le paiement de ${ref(doc)}`, credit: mt, debit: 0 });
        });
      });
    });
    // Trier tous les événements par date pour un solde cohérent (avances + factures + paiements)
    events.sort((a, b) => a.date.getTime() - b.date.getTime());
    const lines: ReleveTransaction[] = [];
    let runningSolde = this.soldeInitial;
    const zeroTnd = this.formatAmount(0) + ' TND';
    lines.push({
      etape: '',
      date: this.formatDateFr(periodStart),
      transaction: 'Solde initial',
      details: '',
      credit: zeroTnd,
      debit: zeroTnd,
      solde: this.formatAmount(runningSolde) + ' TND'
    });
    events.forEach(ev => {
      if (!ev.infoOnly) runningSolde += ev.credit - ev.debit;
      lines.push({
        etape: ev.etape ?? '',
        date: this.formatDateFr(ev.date),
        transaction: ev.transaction,
        details: ev.details,
        credit: ev.credit > 0 ? this.formatAmount(ev.credit) + ' TND' : zeroTnd,
        debit: ev.debit > 0 ? this.formatAmount(ev.debit) + ' TND' : zeroTnd,
        solde: this.formatAmount(runningSolde) + ' TND'
      });
    });
    return lines;
  }

  private normalizePaiementsList(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (response?.data) {
      if (Array.isArray(response.data)) return response.data;
      if (Array.isArray(response.data.paiements)) return response.data.paiements;
      if (response.data.paiement && typeof response.data.paiement === 'object') return [response.data.paiement];
    }
    if (response?.id) return [response];
    return [];
  }

  /** Convertit dd/mm/yyyy en yyyy-mm-dd pour l'API. */
  private dateToApi(ddmmyyyy: string): string {
    const d = this.parseDateFr(ddmmyyyy);
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  /** Formate date_operation (ISO) en dd/mm/yyyy. */
  private formatDateOperation(iso: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return this.formatDateFr(d);
  }

  private formatDateFr(d: Date): string {
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  /** Parse "dd/mm/yyyy" en Date */
  private parseDateFr(str: string): Date | null {
    if (!str || str.length < 10) return null;
    const [dd, mm, yyyy] = str.split('/').map(Number);
    if (!dd || !mm || !yyyy) return null;
    return new Date(yyyy, mm - 1, dd);
  }

  /** Met à jour la période à partir d'une plage de dates (Du - Au) */
  private updatePeriodFromRange(start: Date, end: Date): void {
    this.dateDu = this.formatDateFr(start);
    this.dateAu = this.formatDateFr(end);
    this.selectedDate = new Date(start);
    this.selectedMonthIndex = start.getMonth();
    this.selectedYear = start.getFullYear();
    this.selectedMonth = start.getMonth() + 1;
    this.monthLabel = MONTHS_FR[start.getMonth()];
    this.periodShort = `${String(start.getDate()).padStart(2, '0')}/${String(this.selectedMonth).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}/${String(end.getMonth() + 1).padStart(2, '0')}`;
    this.selectedPeriod = `Mois : ${this.monthLabel} ${this.selectedYear} ${this.periodShort}`;
    if (this.client?.id) this.loadReleveFromDocuments();
  }

  private updatePeriodFromSelection(): void {
    const d = this.selectedDate;
    this.selectedMonth = d.getMonth() + 1;
    this.selectedYear = d.getFullYear();
    this.selectedMonthIndex = d.getMonth();
    this.monthLabel = MONTHS_FR[d.getMonth()];
    let firstDay: Date;
    let lastDay: Date;
    if (this.viewMode === 'annees') {
      firstDay = new Date(this.selectedYear, 0, 1);
      lastDay = new Date(this.selectedYear, 11, 31);
    } else {
      firstDay = new Date(d.getFullYear(), d.getMonth(), 1);
      lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    }
    this.dateDu = this.formatDateFr(firstDay);
    this.dateAu = this.formatDateFr(lastDay);
    // Format 01/04-30/04 (dd/mm-dd/mm)
    this.periodShort = `${String(firstDay.getDate()).padStart(2, '0')}/${String(firstDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}/${String(lastDay.getMonth() + 1).padStart(2, '0')}`;
    if (this.viewMode === 'mois') {
      this.selectedPeriod = `Mois : ${this.monthLabel} ${this.selectedYear} ${this.periodShort}`;
    } else if (this.viewMode === 'annees') {
      this.selectedPeriod = `Année : ${this.selectedYear}`;
    } else {
      this.selectedPeriod = `Mois : ${this.monthLabel} ${this.selectedYear} ${this.periodShort}`;
    }
    if (this.client?.id) this.loadReleveFromDocuments();
  }

  get viewModeLabel(): string {
    if (this.viewMode === 'jours') return 'Jours';
    if (this.viewMode === 'annees') return 'Année';
    return 'Mois';
  }

  generateWeekDays(): void {
    const start = new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth(), 1);
    const startOfWeek = new Date(start);
    startOfWeek.setDate(start.getDate() - start.getDay() + 1);
    this.weekDays = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      const day = d.getDate();
      const month = d.getMonth();
      const year = d.getFullYear();
      const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const today = new Date();
      const isToday = d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
      const label = `${String(day).padStart(2, '0')}/${String(month + 1).padStart(2, '0')}`;
      return { label, dateString, isToday };
    });
  }

  /** Les 7 jours de la semaine contenant selectedDate (pour le popup Jours) */
  get weekDaysForPopup(): { dayName: string; label: string; dateString: string; isToday: boolean }[] {
    const d = new Date(this.selectedDate);
    const dayOfWeek = d.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const startOfWeek = new Date(d);
    startOfWeek.setDate(d.getDate() + mondayOffset);
    const today = new Date();
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const isToday = date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
      const label = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
      return { dayName: this.weekDayNames[i], label, dateString, isToday };
    });
  }

  openPeriodPopup(mode: 'jours' | 'mois' | 'annees'): void {
    this.showViewDropdown = false;
    this.showYearDropdown = false;
    if (this.showPeriodPopup === mode) {
      this.showPeriodPopup = null;
    } else {
      this.showPeriodPopup = mode;
      if (mode === 'jours') {
        this.generateWeekDays();
        // Par défaut : sélectionner aujourd'hui quand on ouvre le popup Jours
        const today = new Date();
        this.selectedDate = new Date(today);
        this.updatePeriodFromRange(today, today);
      } else if (mode === 'annees') {
        // Rafraîchir la liste des années autour de l'année sélectionnée pour l'afficher dans le popup
        this.updateYearsAroundSelected();
      }
    }
    setTimeout(() => Feather.replace(), 0);
  }

  /** Met à jour la liste des années du popup pour qu'elle soit centrée sur l'année sélectionnée. */
  private updateYearsAroundSelected(): void {
    this.years = Array.from({ length: 10 }, (_, i) => this.selectedYear - 5 + i);
  }

  /** Date du jour actuellement sélectionné (pour le popup Jours) au format Y-m-d */
  get selectedDayDateString(): string {
    const d = this.parseDateFr(this.dateDu);
    if (!d) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  closePeriodPopup(): void {
    this.showPeriodPopup = null;
    setTimeout(() => Feather.replace(), 0);
  }

  selectDayInPopup(day: { dateString: string }): void {
    this.selectedDay = day.dateString;
    const [y, m, d] = day.dateString.split('-').map(Number);
    this.selectedDate = new Date(y, m - 1, d);
    this.updatePeriodFromRange(this.selectedDate, this.selectedDate);
    // Synchroniser le calendrier Flatpickr avec la date sélectionnée
    const fp = (this.datePicker?.nativeElement as any)?._flatpickr;
    if (fp) {
      fp.setDate([this.selectedDate, this.selectedDate], false);
    }
    this.closePeriodPopup();
  }

  selectMonthInPopup(index: number): void {
    this.viewMode = 'mois';
    this.selectMonthByIndex(index);
    this.closePeriodPopup();
  }

  /** Dans le popup Mois : changer l'année (< >) */
  changeYearInMonthPopup(delta: number): void {
    this.selectedYear += delta;
    this.selectedDate = new Date(this.selectedYear, this.selectedMonthIndex, 1);
    this.updatePeriodFromSelection();
  }

  selectYearInPopup(y: number): void {
    this.viewMode = 'annees';
    this.selectYear(y);
    this.closePeriodPopup();
  }

  decrementDate(): void {
    if (this.viewMode === 'mois') {
      this.selectedMonthIndex--;
      if (this.selectedMonthIndex < 0) {
        this.selectedMonthIndex = 11;
        this.selectedYear--;
      }
      this.selectedDate = new Date(this.selectedYear, this.selectedMonthIndex, 1);
    } else if (this.viewMode === 'annees') {
      this.selectedYear--;
      this.selectedDate = new Date(this.selectedYear, 0, 1);
    } else {
      this.selectedDate.setDate(this.selectedDate.getDate() - 1);
    }
    this.generateWeekDays();
    this.updatePeriodFromSelection();
  }

  incrementDate(): void {
    if (this.viewMode === 'mois') {
      this.selectedMonthIndex++;
      if (this.selectedMonthIndex > 11) {
        this.selectedMonthIndex = 0;
        this.selectedYear++;
      }
      this.selectedDate = new Date(this.selectedYear, this.selectedMonthIndex, 1);
    } else if (this.viewMode === 'annees') {
      this.selectedYear++;
      this.selectedDate = new Date(this.selectedYear, 0, 1);
    } else {
      this.selectedDate.setDate(this.selectedDate.getDate() + 1);
    }
    this.generateWeekDays();
    this.updatePeriodFromSelection();
  }

  changeViewMode(mode: 'jours' | 'mois' | 'annees'): void {
    this.viewMode = mode;
    if (mode === 'mois') {
      this.selectedMonthIndex = this.selectedDate.getMonth();
      this.selectedYear = this.selectedDate.getFullYear();
    } else if (mode === 'annees') {
      this.selectedYear = this.selectedDate.getFullYear();
    } else {
      this.generateWeekDays();
    }
    this.updatePeriodFromSelection();
    setTimeout(() => Feather.replace(), 0);
  }

  selectMonthByIndex(index: number): void {
    this.selectedMonthIndex = index;
    this.selectedYear = this.selectedDate.getFullYear();
    this.selectedDate = new Date(this.selectedYear, index, 1);
    this.updatePeriodFromSelection();
    setTimeout(() => Feather.replace(), 0);
  }

  selectYear(y: number): void {
    this.selectedYear = y;
    this.selectedDate = new Date(y, this.selectedDate.getMonth(), 1);
    this.selectedMonthIndex = this.selectedDate.getMonth();
    this.updatePeriodFromSelection();
    setTimeout(() => Feather.replace(), 0);
  }

  selectDay(day: { dateString: string }): void {
    this.selectedDay = day.dateString;
    const [y, m, d] = day.dateString.split('-').map(Number);
    this.selectedDate = new Date(y, m - 1, d);
    this.updatePeriodFromSelection();
  }

  toggleDatePicker(): void {
    this.openCalendarOnly();
  }

  toggleViewDropdown(): void {
    this.showViewDropdown = !this.showViewDropdown;
    this.showDatePicker = false;
    this.showYearDropdown = false;
    setTimeout(() => Feather.replace(), 0);
  }

  toggleYearDropdown(): void {
    this.showYearDropdown = !this.showYearDropdown;
    this.showDatePicker = false;
    this.showViewDropdown = false;
    setTimeout(() => Feather.replace(), 0);
  }

  changeYear(delta: number): void {
    this.selectedYear += delta;
    this.selectedDate = new Date(this.selectedYear, this.selectedMonthIndex, 1);
    this.updatePeriodFromSelection();
  }

  get soldeInitialFormatted(): string {
    return this.formatAmount(this.soldeInitial);
  }

  get soldeFormatted(): string {
    return this.formatAmount(this.solde);
  }

  private formatAmount(value: number): string {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
      useGrouping: true
    }).format(value).replace(/\u00A0/g, ' ');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('.period-popup') || target.closest('.date-picker-popup') || target.closest('.dropdown-menu')) return;
    if (this.showDatePicker && target.classList.contains('date-picker-overlay')) {
      this.showDatePicker = false;
    }
    if (!target.closest('.dropdown-wrapper')) {
      this.showViewDropdown = false;
      this.showYearDropdown = false;
    }
    // Ne pas fermer le popup si le clic est sur un bouton Jours/Mois/Année (ils ouvrent le popup)
    if (this.showPeriodPopup && !target.closest('.period-popup-wrapper') && !target.closest('.btn-period')) {
      this.closePeriodPopup();
    }
    // Fermer le calendrier Flatpickr si on clique ailleurs (hors calendrier et hors bouton/input)
    this.closeFlatpickrIfClickOutside(target);
  }

  /** Mousedown en dehors ferme le calendrier (s'exécute avant click, plus fiable). */
  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    this.closeFlatpickrIfClickOutside(target);
  }

  /** Ferme le calendrier Flatpickr lorsque le clic est en dehors du calendrier et du bouton. */
  private closeFlatpickrIfClickOutside(target: HTMLElement): void {
    if (target.closest('.flatpickr-calendar') || target.closest('.calendar-wrapper')) return;
    const fp = this.getFlatpickrInstance();
    if (fp) {
      fp.close();
    }
  }

  /** Récupère l'instance Flatpickr (ViewChild ou input dans le template). */
  private getFlatpickrInstance(): any {
    const input = this.datePicker?.nativeElement ?? this.el.nativeElement.querySelector('.flatpickr-input-releve');
    return input ? (input as any)._flatpickr : null;
  }
}
